/* eslint-disable complexity */
import { FormattedMessage, useIntl } from 'react-intl';
import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Typography from 'carbon-react/lib/components/typography';
import Link from 'carbon-react/lib/components/link';
import Textbox from 'carbon-react/lib/components/textbox';
import Button from 'carbon-react/lib/components/button';

import { IMAGE_URLS } from '@futrli/ui-components';
import {
  HostedUIIdentityProvider,
  interpolatePathParams,
  PREDICT_PRODUCT_SLUG,
  ROUTE_PARAMS,
  useWindowSize
} from '@futrli/shared-services';
import { AnyError } from '@futrli/errors';

import { LeftBanner } from './left-banner';
import * as styles from '../../core/common.scss';
import { RightBanner } from './right-banner';
import { getFormValidator } from '../../utils/validation-utils';
import { DESKTOP_LAYOUT_LIMIT, FORM_FIELDS, LOGIN_ROUTE, REGISTER_FORM_ID } from '../../core/constants';
import { HandleFederatedSignIn } from '../../federated-sign-in/use-federated-sign-in';
import { AuthError } from '../../core/types';
import { getInviteErrorMessage } from '../../utils/invite';
import { getRegisterErrorMessage } from '../../utils/register';

export interface SageRegisterFormProps {
  isLoading: boolean;
  invitedUser?: boolean;
  initialEmail?: string;
  federatedSignInError?: AnyError;
  sageIdError?: AuthError;
  onFederatedSignUp?: HandleFederatedSignIn;
  hideForm?: boolean;
  onSubmit: (values: { username: string }) => void;
}

type RegisterErrorData = Record<string, ReactNode>;

type RegisterTouchedValues = Record<string, boolean>;

export interface SageRegisterFormData {
  username: string;
}

export const SageRegisterForm = ({
  isLoading,
  invitedUser,
  initialEmail,
  federatedSignInError,
  sageIdError,
  onFederatedSignUp,
  onSubmit,
  hideForm
}: SageRegisterFormProps) => {
  const [touchedValues, setTouchedValues] = useState<RegisterTouchedValues>({
    username: false
  });

  const [formValues, setFormValues] = useState<SageRegisterFormData>({
    username: initialEmail || ''
  });

  const { width: windowWidth } = useWindowSize();

  useEffect(() => {
    setFormValues({
      username: initialEmail || ''
    });
    setErrors(
      validate({
        username: initialEmail || ''
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEmail]);

  useEffect(() => {
    if (typeof windowWidth === 'number' && windowWidth >= DESKTOP_LAYOUT_LIMIT) {
      document.body.classList.add(styles.bodyStyles);
    }

    return () => {
      if (typeof windowWidth === 'number' && windowWidth >= DESKTOP_LAYOUT_LIMIT) {
        document.body.classList.remove(styles.bodyStyles);
      }
    };
  }, [windowWidth]);

  const intl = useIntl();

  const navigate = useNavigate();

  const validate = getFormValidator([FORM_FIELDS.username], REGISTER_FORM_ID, intl);

  const [errors, setErrors] = useState<RegisterErrorData>(validate((formValues as unknown) as Record<string, string>));

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormValues({
        username: e.target.value
      });
      setErrors(
        validate({
          username: e.target.value
        })
      );
      setTouchedValues({ username: true });
    },
    [validate]
  );

  const handleBlur = useCallback(() => {
    setTouchedValues({
      username: true
    });
  }, []);

  const handleLoginClick = useCallback(
    () => navigate(interpolatePathParams(LOGIN_ROUTE, { [ROUTE_PARAMS.PRODUCT_SLUG]: PREDICT_PRODUCT_SLUG })),
    [navigate]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const disabled = isLoading || Object.keys(errors).length > 0;

    if (e.key === 'Enter' && !disabled) {
      onSubmit(formValues);
    }
  };

  const handleFederatedSignUp = useCallback(
    (customProvider: keyof typeof HostedUIIdentityProvider) => {
      return (event: React.MouseEvent) => {
        event.preventDefault();

        onFederatedSignUp?.(customProvider);
      };
    },
    [onFederatedSignUp]
  );

  const handleSubmit = useCallback(
    (_e: React.MouseEvent) => {
      onSubmit(formValues);
    },
    [formValues, onSubmit]
  );

  const dataError = Object.keys(errors).length > 0;

  const submitFormDisabled = isLoading || dataError;

  const shouldRenderForm = !hideForm;

  return (
    <main className={styles.newPageContainer}>
      <div className={styles.mobileHeight}>
        <div className={styles.newPageContent}>
          <LeftBanner invitedUser={invitedUser} />
          <div>
            <div className={styles.registerFormContainer}>
              <Typography variant="h1" marginBottom={24}>
                {invitedUser ? (
                  <FormattedMessage id="register.invited" defaultMessage="You've been invited" />
                ) : (
                  <FormattedMessage id="register.set_up_your_account" defaultMessage="Set up your account" />
                )}
              </Typography>
              {invitedUser ? null : (
                <Typography fontSize="16px" mt={24}>
                  <FormattedMessage
                    id="register.already_have_account_link"
                    defaultMessage="Already have an account? {link}"
                    values={{
                      link: (
                        <Link onClick={handleLoginClick} className={styles.sageCarbonAuthLink}>
                          {intl.formatMessage({ id: 'register.sign_in', defaultMessage: 'Sign in' })}
                        </Link>
                      )
                    }}
                  />
                </Typography>
              )}
              {shouldRenderForm ? (
                <div>
                  <Textbox
                    size="large"
                    label={intl.formatMessage({
                      id: 'common.email_address',
                      defaultMessage: 'Email address'
                    })}
                    placeholder={intl.formatMessage({
                      id: 'common.enter_email_address',
                      defaultMessage: 'Enter your email address'
                    })}
                    value={formValues.username}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    name={FORM_FIELDS.username}
                    error={!!errors.username && touchedValues.username ? (errors.username as string) : ''}
                    disabled={isLoading || invitedUser}
                    mb={16}
                  />

                  <>
                    <Button
                      fullWidth
                      disabled={submitFormDisabled}
                      mb={16}
                      onClick={handleSubmit}
                      buttonType="primary"
                      size="large"
                    >
                      {invitedUser ? (
                        <FormattedMessage id="register.continue_invitation" defaultMessage="Continue invitation" />
                      ) : (
                        <FormattedMessage id="register.continue_registration" defaultMessage="Continue registration" />
                      )}
                    </Button>
                    {!invitedUser ? (
                      <>
                        <Typography fontSize="16px" textAlign="center" marginBottom={16}>
                          <FormattedMessage id="common.or" defaultMessage="or" />
                        </Typography>
                        <Button
                          buttonType="secondary"
                          className={styles.newXeroButtonSageCarbon}
                          fullWidth
                          disabled={isLoading}
                          onClick={handleFederatedSignUp(HostedUIIdentityProvider.Xero)}
                        >
                          <Typography fontWeight="500" mb={0}>
                            <img src={IMAGE_URLS.XERO_LOGO} alt="xero-logo" />
                            <FormattedMessage id="register.sign_up_with_xero" defaultMessage="Sign up with Xero" />
                          </Typography>
                        </Button>
                      </>
                    ) : null}
                  </>
                </div>
              ) : null}
              {(sageIdError || federatedSignInError) && (
                <Typography my={24} fontSize="14px" color="errorRed">
                  {invitedUser ? getInviteErrorMessage(sageIdError) : getRegisterErrorMessage(sageIdError)}
                </Typography>
              )}
            </div>
            <div className={styles.bottomMargin} />
          </div>
          <RightBanner />
        </div>
      </div>
    </main>
  );
};
